// src/contexts/ProfilePictureContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const ProfilePictureContext = createContext();

// Some avatar URLs were persisted HTML-entity-encoded (e.g. the query
// separator saved as "&amp;"), which corrupts the Storage download token and
// makes the image 403. Decode the common entities so the URL is valid again.
export const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  return url
    .replace(/&amp;/g, '&')
    .replace(/&#38;/g, '&')
    .replace(/&#x26;/gi, '&');
};

/**
 * Avatar store — derives image from AuthContext's canonical userProfile.
 * No extra users/{uid} getDoc (Auth already loaded the profile).
 */
export function ProfilePictureProvider({ children, currentUser, userProfile }) {
  const [imageUrl, setImageUrl] = useState('');
  const [authPhotoUrl, setAuthPhotoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setImageUrl('');
      setAuthPhotoUrl('');
      setError(null);
      setIsLoading(false);
      return;
    }

    const authPhoto = sanitizeImageUrl(currentUser.photoURL || '');
    setAuthPhotoUrl(authPhoto);
    setError(null);

    // Wait for Auth's profile load rather than issuing a parallel getDoc
    if (userProfile === undefined) {
      setIsLoading(true);
      return;
    }

    const stored = userProfile?.profileImageUrl || '';
    setImageUrl(sanitizeImageUrl(stored) || authPhoto);
    setIsLoading(false);
  }, [currentUser, userProfile]);

  return (
    <ProfilePictureContext.Provider value={{ imageUrl, authPhotoUrl, setImageUrl, isLoading, error }}>
      {children}
    </ProfilePictureContext.Provider>
  );
}

ProfilePictureProvider.propTypes = {
  children: PropTypes.node.isRequired,
  currentUser: PropTypes.object,
  userProfile: PropTypes.object,
};

export function useProfilePicture() {
  return useContext(ProfilePictureContext);
}
